import React, { useState, useEffect } from 'react';
import { InvoiceStaging } from './InvoiceStaging';
import { QuickbooksIntegration } from './QuickbooksIntegration';
import { MiniNav, Instructions } from './FieldComponents';
import '../styles/AccountingPortal.css';
import { AwaitingAction } from './AwaitingAction';
import { ReceiptReview } from './ReceiptReview';

export const AccountingPortal = () => {
    const [activeTab, setActiveTab] = useState('invoiceStaging');
    const [user, setUser] = useState(null);
    const [qbConnected, setQbConnected] = useState(false);
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState({
        awaitingAction: 0,
        requestedInvoices: 0,
        receiptReview: 0
    });

    const fetchCounts = async () => {
        try {
            const [invoiceResult, receiptResult] = await Promise.allSettled([
                fetch('https://api.casedb.co/invoice-cases.php?count=true', { credentials: 'include' }),
                fetch('https://api.casedb.co/expense-receipts.php?action=summary&time=' + Date.now(), { credentials: 'include' })
            ]);
            if (invoiceResult.status !== 'fulfilled') {
                throw invoiceResult.reason;
            }
            const data = await invoiceResult.value.json();
            const receiptData = receiptResult.status === 'fulfilled'
                ? await receiptResult.value.json().catch(() => ({}))
                : {};

            if (data.success) {
                setCounts({
                    awaitingAction: data.awaiting_count,
                    requestedInvoices: data.requested_count,
                    receiptReview: Number(receiptData?.summary?.pending || 0) + Number(receiptData?.summary?.unmatched || 0)
                });
            }
        } catch (error) {
            console.error('Error fetching counts:', error);
        }
    };

    useEffect(() => {
        const fetchUser = async () => {
        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/user.php', { credentials: 'include' });
            const data = await response.json();
            
            if (data.success) {
                // Check if user has accounting portal access
                if (Number(data.user?.accounting_portal) !== 1) {
                    // Redirect to home page if no access
                    window.location.href = 'https://casedb.co/';
                    return;
                }
                
                setUser(data.user);
                
                const qbResponse = await fetch('https://api.casedb.co/accounting.php?action=qb_status', { credentials: 'include' });
                const qbData = await qbResponse.json();
                setQbConnected(qbData.connected || false);
            } else {
                // If no valid session, redirect to home
                window.location.href = 'https://casedb.co/';
            }
        } catch (error) {
            console.error('Error fetching user information:', error);
            // On error, redirect to home for safety
            window.location.href = 'https://casedb.co/';
        } finally {
            setLoading(false);
        }
        };

        fetchUser();
        fetchCounts();
    }, []);

    const renderContent = () => {
        if (loading) {
            return <div className="loading-container">Verifying access...</div>;
        }

        switch (activeTab) {
            case 'invoiceStaging':
                return <InvoiceStaging user={user} qbConnected={qbConnected} fetchCounts={fetchCounts} />;
            case 'action':
                return <AwaitingAction fetchCounts={fetchCounts} />;
            case 'receiptReview':
                return <ReceiptReview onSummaryChange={(nextSummary) => setCounts((current) => ({
                    ...current,
                    receiptReview: Number(nextSummary?.pending || 0) + Number(nextSummary?.unmatched || 0)
                }))} />;
            case 'quickbooks':
                return <QuickbooksIntegration user={user} connected={qbConnected} setConnected={setQbConnected} />;
            case 'reports':
                return (
                    <div className="accounting-section">
                        <Instructions instructions="Financial reports functionality is coming soon. This will allow you to generate P&L statements, balance sheets, and other financial reports." />
                    </div>
                );
            case 'settings':
                return (
                    <div className="accounting-section">
                        <Instructions instructions="Accounting settings functionality is coming soon. This will allow you to configure account mappings, default settings, and user permissions." />
                    </div>
                );
            default:
                return <InvoiceStaging user={user} qbConnected={qbConnected} />;
        }
    };

    const menuOptions = [
        'Invoice Staging',
        'Awaiting Action',
        'Receipt Review',
        'Financial Reports',
        'Accounting Settings'
    ];

    return (
        <>
            <div id="page-header">
                <h2>Accounting Portal</h2>
                {!qbConnected && activeTab !== 'quickbooks' && (
                <button 
                    className="action alt" 
                    onClick={() => setActiveTab('quickbooks')}
                >
                    Connect QuickBooks
                </button>
                )}
            </div>
            <div className='accounting-page'>
                <MiniNav 
                    options={menuOptions} 
                    selectedOption={
                        activeTab === 'invoiceStaging' ? 0 : 
                        activeTab === 'action' ? 1 :
                        activeTab === 'receiptReview' ? 2 :
                        activeTab === 'reports' ? 3 : 4
                    } 
                    setSelectedOption={(index) => {
                        setActiveTab(
                            index === 0 ? 'invoiceStaging' : 
                            index === 1 ? 'action' :
                            index === 2 ? 'receiptReview' :
                            index === 3 ? 'reports' : 'settings'
                        );
                    }}
                    notifications={[counts.awaitingAction, counts.requestedInvoices, counts.receiptReview]}
                />
                <div className="accounting-content">
                    {renderContent()}
                </div>
            </div>
        </>
    );
};

export default AccountingPortal;
